import { Select, Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import { Button } from '../../components/button';
import { CreateButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { SearchInput } from '../../components/search-input';
import { TableHeaderWithSubtitle } from '../../components/table-header-with-subtitle';
import { Tag } from '../../components/tag';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useGamesStore,
} from '../../contexts/mobx';
import { GamesSearchParams } from '../../stores/games';
import { ExternalGame } from '../../types/games';
import { GameRegionTag, getGamesLocaleItem } from '../../utils/games';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import style from './style.module.scss';

const renderIsActive = (_: unknown, record: ExternalGame) => {
    return record.status === 'active' ? (
        <Tag variant="success">Active</Tag>
    ) : (
        <Tag variant="error">Inactive</Tag>
    );
};

const renderThumbnail = (_: unknown, record: ExternalGame) => {
    const enImgSrc = getGamesLocaleItem(record.image, 'en');
    return (
        <img key="en" src={enImgSrc ?? ''} alt="" className={style.thumbnail} />
    );
};

const renderTitle = (_: unknown, record: ExternalGame) => {
    const enTitle = getGamesLocaleItem(record.title, 'en');
    return <div className={style.title}>{enTitle}</div>;
};

const renderRegion = (_: unknown, record: ExternalGame) => {
    return <GameRegionTag region={record.provinces || []} />;
};

const renderDates = (_: unknown, record: ExternalGame) => {
    return (
        <div className={'dates_holder'} style={{ padding: '4px 0' }}>
            <div
                className="text_center inline_block"
                style={{ marginRight: '12px' }}
            >
                <div style={{ marginBottom: '2px' }}>
                    {moment(record.startDate).format('ll')}
                </div>
                <div className={'lil_label'}>
                    {moment(record.startDate).format('LT')}
                </div>
            </div>
            {record.endDate != null && (
                <div className="text_center inline_block">
                    <div style={{ marginBottom: '2px' }}>
                        {moment(record.endDate).format('ll')}
                    </div>
                    <div className={'lil_label'}>
                        {moment(record.endDate).format('LT')}
                    </div>
                </div>
            )}
        </div>
    );
};

const columns: ColumnProps<ExternalGame>[] = [
    {
        title: 'Status',
        key: 'is_active',
        render: renderIsActive,
        defaultSortOrder: 'ascend',
        className: style.statusColumn,
        align: 'center',
        width: 104,
        sorter: {
            compare: (a, b) => {
                return a.status.localeCompare(b.status);
            },
            multiple: 2,
        },
    },
    {
        title: 'Image',
        key: 'image',
        render: renderThumbnail,
        align: 'center',
        width: 106,
    },
    {
        title: 'Title',
        key: 'title',
        render: renderTitle,
    },
    {
        title: 'Region',
        key: 'region',
        render: renderRegion,
    },
    {
        title: (
            <TableHeaderWithSubtitle
                title="Dates"
                subtitle="Start date / End date"
            />
        ),
        key: 'dates',
        render: renderDates,
        defaultSortOrder: 'descend',
        sorter: {
            compare: (a, b) =>
                moment(a.endDate).unix() - moment(b.endDate).unix(),
            multiple: 1,
        },
    },
];

export const GamesPage = observer(() => {
    const gamesStore = useGamesStore();
    const authStore = useAuthStore();
    const applicationSelectionStore = useApplicationSelectionStore(); // Used for restricting the games to the selected application
    const navigation = useNavigation();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const search = useSearchParams<GamesSearchParams>();

    React.useEffect(() => {
        gamesStore.fetchGames(search.params);
        gamesStore.filterGames(search.params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gamesStore, applicationSelectionStore.selectedApplication]);

    React.useEffect(() => {
        gamesStore.filterGames(search.params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search.params]);

    const clearFilters = () => {
        search.setParams({
            status: undefined,
            title: undefined,
            type: undefined,
        });
    };

    return (
        <>
            <PageTopBar title="Games">
                <>
                    <CreateButton
                        onClick={() => navigation.goToGameCreate('external')}
                    >
                        Create game
                    </CreateButton>
                </>
            </PageTopBar>

            <div className="action_zone_above_table">
                <div
                    className={`${style.selects_row} table_header_options_bar`}
                >
                    <SearchInput
                        searchValue={search.params.title}
                        onChange={(e) =>
                            search.setParams({ title: e.target.value })
                        }
                        allowClear={true}
                        placeholder="Search"
                    />

                    <Select<string>
                        placeholder="Status"
                        value={search.params.status}
                        onChange={(v) => search.setParams({ status: v })}
                        allowClear
                    >
                        <Select.Option value="active">Active</Select.Option>
                        <Select.Option value="inactive">Inactive</Select.Option>
                    </Select>
                    <Button
                        variant="secondary-destructive"
                        onClick={clearFilters}
                    >
                        Clear Filters
                    </Button>
                </div>
            </div>

            <Table<ExternalGame>
                className="clickable_table_body_rows"
                rowKey="id"
                dataSource={gamesStore.filteredGames ?? undefined}
                columns={columns}
                pagination={false}
                loading={gamesStore.isFetchingCount > 0}
                onRow={(r) => ({
                    onClick: () => navigation.goToGameEdit(r.id, r.type),
                })}
            />
        </>
    );
});
